import type { TFunction } from 'i18next';

/**
 * Internal views of the Agent Miner page, tracked via the `tab` search param.
 * The AI-adoption views (Model Comparison, Delivery Impact, Task Fit, Adoption)
 * sit behind the `ai_adoption` feature flag and are powered by the same
 * agent-miner session feed as the default Sessions view, so they reuse its
 * domain enums (`AgentSource`, `AgentSessionLabel`) rather than redefining them.
 */
enum AgentMinerTab {
  ModelComparison = 'model-comparison',
  DeliveryImpact = 'delivery-impact',
  TaskFit = 'task-fit',
  Adoption = 'adoption',
  Sessions = 'sessions',
}

/** Display metadata for a single Agent Miner tab button. */
interface AgentMinerTabDef {
  id: AgentMinerTab;
  /** Static label, or a resolver when the label depends on org terminology. */
  label: string | ((t: TFunction) => string);
  /** Material symbol icon name. */
  icon: string;
}

/**
 * Tabs surfaced only when the `ai_adoption` feature flag is enabled, in display
 * order (they render ahead of Sessions in the control bar). Task Fit's label
 * follows the org's configurable work-unit term (`common.work_unit`).
 */
const AI_ADOPTION_TABS: AgentMinerTabDef[] = [
  { id: AgentMinerTab.ModelComparison, label: 'Model Comparison', icon: 'analytics' },
  { id: AgentMinerTab.DeliveryImpact, label: 'Delivery Impact', icon: 'trending_up' },
  { id: AgentMinerTab.TaskFit, label: (t) => `${t('common.work_unit')} Fit`, icon: 'checklist' },
  { id: AgentMinerTab.Adoption, label: 'Adoption', icon: 'groups' },
];

/**
 * Resolves a tab's display label, applying the i18n resolver when the label
 * depends on org terminology.
 *
 * @param {AgentMinerTabDef} tab - The tab whose label to resolve
 * @param {TFunction} t - The i18next translation function
 * @returns {string} The label to render
 */
function resolveTabLabel(tab: AgentMinerTabDef, t: TFunction): string {
  return typeof tab.label === 'function' ? tab.label(t) : tab.label;
}

export { AgentMinerTab, AI_ADOPTION_TABS, resolveTabLabel };
export type { AgentMinerTabDef };
